import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useRef } from 'react';
import Loader from 'components/Loader';
import { GECKO_CONSTS, GeckoBtn } from 'ui';
import { useSelector } from 'react-redux';
import AnimatedChatHeads from './AnimatedChatHeads';
import ChatHeads from './ChatHeads';
import {
  fetchAllForContactFilter,
  fetchBotInterferenceConversations,
  fetchClosedConversations,
  fetchContactFilter,
  fetchCustomFilter,
  fetchMyConversations,
  fetchOpenConversations,
  fetchSentConversations,
  fetchTagFilter,
  fetchUnassignedConversations,
  fetchUndeliveredMessagesConversations,
  fetchSavedCustomFilter,
} from '../../actions';
import {
  getChannels,
  getCurrentFilter,
  getFetching,
  getNextPage,
  getPage,
  getPerPage,
  getScreenSize,
  getQuickFilter,
} from '../../selectors';
import { getLoggedInUser } from 'selectors/app_state';
import { getLoggedInUsersTeams } from 'selectors/teams';
import { getPinnedSavedCustomFilters } from 'selectors';
import CONSTS from 'constants/index.js';
import ConversationQuickFilterDropdown from './ConversationQuickFilterDropdown';
import NoConversations from './NoConversations';
import './index.scss';

const { QUICK_FILTERS } = CONSTS.CONVERSATION_FILTERS;

const anyFilterButUnassigned = (currentFilter) =>
  currentFilter !== CONSTS.FILTER_UNASSIGNED;

const filterIsOneOf = (filters) => (currentFilter) =>
  filters.includes(currentFilter);

const openOrMyConversationsFilter = filterIsOneOf([
  CONSTS.FILTER_OPEN,
  CONSTS.FILTER_MY_CONVERSATIONS,
]);

const usePrevious = (value) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const ChatHeadsContainer = (props) => {
  const page = useSelector(getPage);
  const channels = useSelector(getChannels);
  const currentFilter = useSelector(getCurrentFilter);
  const previousFilter = usePrevious(currentFilter);
  const fetching = useSelector(getFetching);
  const nextPage = useSelector(getNextPage);
  const perPage = useSelector(getPerPage);
  const screenSize = useSelector(getScreenSize);
  const selectedQuickFilter = useSelector(getQuickFilter);
  const teams = useSelector(getLoggedInUsersTeams);
  const loggedInUser = useSelector(getLoggedInUser);
  const savedCustomFilters = useSelector(getPinnedSavedCustomFilters);

  const teamQuickFilters = teams.map((team) => {
    return {
      label: team.name,
      shouldShowInFilter: anyFilterButUnassigned,
      value: {
        type: QUICK_FILTERS.ASSIGNED_TO_TEAM,
        teamId: team.id,
      },
    };
  });

  const quickFilters = [
    {
      label: 'All conversations',
      shouldShowInFilter: anyFilterButUnassigned,
      value: null,
    },
    {
      label: 'Team assigned: awaiting agent',
      shouldShowInFilter: openOrMyConversationsFilter,
      value: {
        type: QUICK_FILTERS.TEAM_ASSIGNED_AWAITING_AGENT,
      },
    },
    {
      label: 'Waiting for response',
      shouldShowInFilter: anyFilterButUnassigned,
      value: {
        type: QUICK_FILTERS.AWAITING_RESPONSE,
      },
    },
    {
      label: 'Your Assignments',
      shouldShowInFilter: anyFilterButUnassigned,
      options: [
        {
          label: 'Directly to you',
          shouldShowInFilter: anyFilterButUnassigned,
          value: {
            type: QUICK_FILTERS.ASSIGNED_DIRECTLY_TO_AGENT,
            userId: loggedInUser.id,
          },
        },
        ...teamQuickFilters,
      ],
    },
  ];

  const {
    activeConversation,
    changeConversationStatus,
    conversations,
    deleteConversation,
    ignoreConversation,
    handleClick,
    sort,
    openFilterModal,
  } = props;

  const fetchFilter = useCallback(
    (options) => {
      const filterMap = {
        [CONSTS.FILTER_MY_CONVERSATIONS]: fetchMyConversations,
        [CONSTS.FILTER_UNASSIGNED]: fetchUnassignedConversations,
        [CONSTS.FILTER_OPEN]: fetchOpenConversations,
        [CONSTS.FILTER_CLOSED]: fetchClosedConversations,
        [CONSTS.FILTER_SENT]: fetchSentConversations,
        [CONSTS.FILTER_ASSIGNED_TO_BOT]: fetchBotInterferenceConversations,
        [CONSTS.FILTER_CONTACT_OPEN]: fetchContactFilter,
        [CONSTS.FILTER_CONTACT]: fetchAllForContactFilter,
        [CONSTS.FILTER_TAG]: fetchTagFilter,
        [CONSTS.FILTER_CUSTOM_FILTER]: fetchCustomFilter,
        [CONSTS.FILTER_UNDELIVERED]: fetchUndeliveredMessagesConversations,
        ...savedCustomFilters.reduce((filters, customFilter) => {
          return {
            ...filters,
            [customFilter.id]: (props) =>
              fetchSavedCustomFilter({
                ...props,
                id: customFilter.id,
                filters: customFilter.filters,
              }),
          };
        }, {}),
      };
      return filterMap[currentFilter] ? filterMap[currentFilter](options) : null;
    },
    [currentFilter]
  );

  const getConversationsPage = useCallback(
    (pageNumber) => {
      return fetchFilter({
        page: pageNumber,
        sort: sort,
        addToExisting: true,
        quickFilter: selectedQuickFilter,
      });
    },
    [sort, fetchFilter, selectedQuickFilter]
  );

  const hasFilterChanged =
    previousFilter !== null && previousFilter !== currentFilter;
  useEffect(() => {
    // When user action changes amount of conversations chat heads in view,
    // this will check if we can pull in the next one.
    const canLoadMoreConversations =
      page && conversations.length < perPage && nextPage !== page;
    if (hasFilterChanged === false && canLoadMoreConversations) {
      getConversationsPage(1);
    }
  }, [
    conversations,
    page,
    perPage,
    nextPage,
    getConversationsPage,
    hasFilterChanged,
  ]);

  const changeSort = (newSort) => {
    if (sort === newSort) {
      return;
    }
    return fetchFilter({
      page: 1,
      addToExisting: false,
      sort: newSort,
      quickFilter: selectedQuickFilter,
    });
  };
  const toggleSort = () => {
    const newSort = sort === 'ASC' ? 'DESC' : 'ASC';
    return changeSort(newSort);
  };

  const renderChatHeads = () => {
    if (conversations.length === 0 && activeConversation !== null) {
      return (
          <NoConversations openFilterModal={openFilterModal} />
      );
    }

    const chatHeadProps = {
      activeConversation,
      changeConversationStatus,
      channels,
      conversations,
      deleteConversation,
      ignoreConversation,
      handleClick,
      screenSize,
      currentFilter,
    };

    const MAX_CONVERSATIONS_FOR_ANIMATION = 50;
    if (conversations.length < MAX_CONVERSATIONS_FOR_ANIMATION) {
      return <AnimatedChatHeads {...chatHeadProps} />;
    }
    return <ChatHeads {...chatHeadProps} />;
  };

  const applyQuickFilter = (filterName) => {
    return fetchFilter({
      page: 1,
      addToExisting: false,
      sort: sort,
      quickFilter: filterName,
    });
  };

  const renderChatHeadsHeader = () => {
    if (conversations.length || selectedQuickFilter !== null) {
      return (
        <div className="ChatHeadsContainer-header">
          <div className="ChatHeadsContainer-header-sort">
            <ConversationQuickFilterDropdown
              quickFilters={quickFilters}
              currentFilter={currentFilter}
              onFilterSelect={applyQuickFilter}
              selectedQuickFilter={selectedQuickFilter}
            />
            <GeckoBtn
              handler={() => toggleSort()}
              type={GECKO_CONSTS.BTN_TYPE_NAKED}
              name={sort === 'ASC' ? 'Oldest' : 'Newest'}
              icon={
                sort === 'ASC'
                  ? 'fas fa-sort-amount-down'
                  : 'fas fa-sort-amount-up'
              }
            />
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="ChatHeadsContainer">
      {renderChatHeadsHeader()}
      <div className="ChatHeadsContainer-chatheads" role="list">
        {renderChatHeads()}
        {nextPage !== page ? (
          <div className="LoadMore">
            <Loader isLoading={fetching}>
              <GeckoBtn
                handler={() => getConversationsPage(nextPage)}
                name="Load More"
              />
            </Loader>
          </div>
        ) : null}
      </div>
    </div>
  );
};

ChatHeadsContainer.defaultProps = {
  conversations: [],
  handleClick: () => {},
};

ChatHeadsContainer.propTypes = {
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  screenSize: PropTypes.string,
  activeConversation: PropTypes.number,
  handleClick: PropTypes.func,
  sort: PropTypes.oneOf(['ASC', 'DESC']),
  onSortChange: PropTypes.func,
  channels: PropTypes.arrayOf(PropTypes.shape()),
};

export default ChatHeadsContainer;
